import { Trans } from '@lingui/macro';
import { MultipleCheckbox } from './MultipleCheckbox';
import { MetricField, MetricUnit, Reports_GetDevicesQuery } from '@/__generated__/graphql';
import { FC, useEffect, useMemo, useState } from 'react';
import { ALLOWED_METRIC_FIELDS } from '@features/reports2/utils/allowedMetricFields';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import { getMetricFieldAttributes } from '@/utils/attributes/metricField';
import { useLingui } from '@lingui/react';
import Fuse from 'fuse.js';

interface Props {
  data: Reports_GetDevicesQuery;
  selectedDeviceIds: string[];
  selectedMetrics: Metric[];
  onChangeSelectedMetrics: (metrics: Metric[]) => void;
}

type Metric = {
  name: MetricField;
  unit: MetricUnit | null;
};

type MetricOption = {
  name: MetricField;
  label: string;
  unit: MetricUnit | null;
  options: { label: string; value: MetricUnit }[];
};

export const MetricsInput: FC<Props> = ({
  data,
  selectedDeviceIds,
  selectedMetrics,
  onChangeSelectedMetrics,
}) => {
  const { i18n } = useLingui();
  const [searchMetrics, setSearchMetrics] = useState('');
  const [metricOptions, setMetricOptions] = useState<MetricOption[]>([]);

  useEffect(() => {
    const fieldsSet = new Set<MetricField>();
    data?.devices
      .filter((device) => selectedDeviceIds.includes(device.id))
      .forEach(({ metrics }) => {
        metrics.availableFields.forEach((field) => {
          if (ALLOWED_METRIC_FIELDS.includes(field)) {
            fieldsSet.add(field);
          }
        });
      });

    setMetricOptions((oldOptions) => {
      const fields = Array.from(fieldsSet);

      const newOptions = fields.map((field) => {
        const units = getUnitsPerMetric(field, i18n);
        const oldOptionUnit = oldOptions.find((option) => option.name === field)?.unit ?? null;
        let defaultUnit = units.at(0)?.enumValue ?? null;
        if (
          [
            MetricField.Level,
            MetricField.OriginalDistance,
            MetricField.OriginalDistanceRaw,
            MetricField.Position,
            MetricField.LevelRaw,
            MetricField.SnowLevel,
            MetricField.SnowLevelRaw,
            MetricField.SnowLevelNonCalibrated,
            MetricField.SnowWaterEquivalent,
            MetricField.SnowWaterEquivalentRaw,
            MetricField.SnowWaterEquivalentNonCalibrated,
          ].includes(field)
        ) {
          defaultUnit = MetricUnit.Centimeter;
        }
        if (
          [
            MetricField.VolumeM3,
            MetricField.VolumeDay,
            MetricField.VolumeHour,
            MetricField.VolumeMonth,
            MetricField.StoredVolume,
            MetricField.CalculatedVolume,
            MetricField.AccumulatedVol,
            MetricField.AccumulatedVolRaw,
            MetricField.OriginalVol,
            MetricField.OriginalVolRaw,
            MetricField.AccumulatedVolume,
            MetricField.DgaAccumulatedVolume,
          ].includes(field)
        ) {
          defaultUnit = MetricUnit.CubicMeter;
        }
        const label = getMetricFieldAttributes(field, i18n).dataLabel;

        return {
          name: field,
          label: label[0].toUpperCase() + label.slice(1),
          unit: oldOptionUnit ?? defaultUnit,
          options: units.map((unit) => ({
            label: unit.label,
            value: unit.enumValue,
          })),
        };
      });

      return newOptions;
    });
  }, [data, selectedDeviceIds, i18n]);

  const filteredMetricOptions = useMemo(() => {
    let filteredItems = metricOptions.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
    if (searchMetrics) {
      const fuse = new Fuse(filteredItems, {
        keys: ['label'],
      });
      filteredItems = fuse.search(searchMetrics).map(({ item }) => item);
    }

    return filteredItems;
  }, [metricOptions, searchMetrics]);

  return (
    <MultipleCheckbox
      title={<Trans>Seleccionar métricas</Trans>}
      options={filteredMetricOptions.map((option) => {
        const selectedMetric = selectedMetrics.find((metric) => option.name === metric.name);

        return {
          value: option.name,
          label: option.label,
          checked: selectedMetric !== undefined,
          select: {
            options: option.options,
            value: selectedMetric?.unit || option.unit,
            onChange(unit: MetricUnit, idx: number) {
              setMetricOptions((oldOptions) => {
                const { name } = filteredMetricOptions[idx] ?? {};
                return oldOptions.map((option) => {
                  if (option.name === name) {
                    return { ...option, unit };
                  } else {
                    return option;
                  }
                });
              });

              onChangeSelectedMetrics(
                selectedMetrics.map((metric) => {
                  if (metric.name === option.name) {
                    return { ...metric, unit };
                  } else {
                    return { ...metric };
                  }
                }),
              );
            },
          },
        };
      })}
      onToggle={(value, checked) => {
        const newMetrics = selectedMetrics.filter((metric) => metric.name !== value);
        if (checked) {
          const metricOption = metricOptions.find((option) => option.name === value);
          if (metricOption) {
            onChangeSelectedMetrics([
              ...newMetrics,
              {
                name: metricOption.name,
                unit: metricOption.unit,
              },
            ]);
          }
        } else {
          onChangeSelectedMetrics([...newMetrics]);
        }
      }}
      deselectAllDisabled={selectedMetrics.length === 0}
      selectAllDisabled={selectedMetrics.length === metricOptions.length}
      onSelectAll={() =>
        onChangeSelectedMetrics(
          metricOptions.map((option) => ({
            name: option.name,
            unit: option.unit,
          })),
        )
      }
      onDeselectAll={() => onChangeSelectedMetrics([])}
      onSearch={setSearchMetrics}
      search={searchMetrics}
      selectedLength={selectedMetrics.length}
    />
  );
};
